import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StarIcon from '@mui/icons-material/Star';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useTheme } from '@mui/material/styles';


// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const TradeAccountsOverview = ({ userID, tradeAccounts }) => {
  const [metrics, setMetrics] = useState({
    totalCapital: 0,
    totalWinRate: 0,
    totalProfitFactor: 0,
    totalTrades: { total: 0, wins: 0, losses: 0, breakevens: 0 },
    bestStrategy: '',
    bestSession: '',
    bestAccount: { name: '', profit: -Infinity },
    worstAccount: { name: '', profit: Infinity },
    averageRR: 0,
    averageDuration: 0,
    mostActiveDay: '',
    consistencyScore: 0,
    equityCurve: { labels: [], data: [] },
  });
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null); // New state for year selection
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    const fetchAggregateData = async () => {
      try {
        const accountsData = {};
        let totalCapital = 0;
        let totalTrades = 0;
        let wins = 0;
        let losses = 0;
        let breakevens = 0;
        let totalProfit = 0;
        let grossProfit = 0;
        let grossLoss = 0;
        let totalRR = 0;
        let rrCount = 0;
        let totalDuration = 0;
        let durationCount = 0;
        const sessionCounts = { Asia: 0, London: 0, NewYork: 0 };
        const dayCounts = Array(7).fill(0);
        const monthlyProfits = {};
        const equityPoints = [];

        for (const account of tradeAccounts) {
          const accountID = account.id || account.tradeAccountID;
          const accountName = account.name || 'Unnamed Account'; // Use user-defined name, fallback to default
          const tradesQuery = query(collection(db, 'Users', userID, 'tradeAccounts', accountID, 'trades'));
          const tradesSnap = await getDocs(tradesQuery);
          const capitalQuery = query(collection(db, 'Users', userID, 'tradeAccounts', accountID, 'capital'));
          const capitalSnap = await getDocs(capitalQuery);

          let accountProfit = 0;
          let accountCapital = 0;
          capitalSnap.forEach(doc => {
            const data = doc.data();
            accountCapital += (data.deposit || 0) - (data.withdrawal || 0);
          });
          totalCapital += accountCapital;

          tradesSnap.forEach(doc => {
            const trade = doc.data();
            const returns = parseFloat(trade.returns || 0);
            totalTrades++;
            if (returns > 0) wins++;
            else if (returns < 0) losses++;
            else breakevens++;
            accountProfit += returns;
            totalProfit += returns;
            if (returns > 0) grossProfit += returns;
            else if (returns < 0) grossLoss += returns;

            const risk = Math.abs(parseFloat(trade.entry) - parseFloat(trade.sl));
            const reward = Math.abs(parseFloat(trade.tp) - parseFloat(trade.entry));
            if (risk && reward) {
              totalRR += reward / risk;
              rrCount++;
            }

            const entryTime = new Date(`${trade.date} ${trade.entryTime}`);
            const exitTime = new Date(`${trade.exitDate} ${trade.exitTime}`);
            const duration = (exitTime - entryTime) / (1000 * 60);
            if (!isNaN(duration)) {
              totalDuration += duration;
              durationCount++;
            }

            const entryHour = entryTime.getHours();
            const session = entryHour >= 0 && entryHour < 8 ? 'Asia' : entryHour >= 8 && entryHour < 16 ? 'London' : 'NewYork';
            sessionCounts[session]++;
            const day = entryTime.getDay();
            dayCounts[day]++;
            const month = entryTime.toISOString().slice(0, 7);
            monthlyProfits[month] = (monthlyProfits[month] || 0) + returns;
          });

          accountsData[accountID] = { name: accountName, profit: accountProfit, trades: tradesSnap.size };
        }

        const totalWinRate = totalTrades ? (wins / totalTrades) * 100 : 0;
        const totalProfitFactor = grossLoss !== 0 ? grossProfit / Math.abs(grossLoss) : (grossProfit > 0 ? Infinity : 0);
        const bestSession = Object.entries(sessionCounts).reduce((a, b) => a[1] > b[1] ? a : b)[0];
        const mostActiveDay = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayCounts.indexOf(Math.max(...dayCounts))];
        const averageRR = rrCount ? totalRR / rrCount : 0;
        const averageDuration = durationCount ? totalDuration / durationCount : 0;
        const bestAccount = Object.entries(accountsData).reduce((a, b) => a[1].profit > b[1].profit ? a : b, ['', { profit: -Infinity, name: '' }]);
        const worstAccount = Object.entries(accountsData).reduce((a, b) => a[1].profit < b[1].profit ? a : b, ['', { profit: Infinity, name: '' }]);
        const sortedMonths = Object.keys(monthlyProfits).sort();
        let runningEquity = totalCapital;
        const equityData = sortedMonths.map(month => {
          runningEquity += monthlyProfits[month];
          return runningEquity;
        });
        const consistencyScore = sortedMonths.filter(month => monthlyProfits[month] > 0).length;
        const bestStrategy = 'N/A';

        setMetrics({
          totalCapital,
          totalWinRate,
          totalProfitFactor,
          totalTrades: { total: totalTrades, wins, losses, breakevens },
          bestStrategy,
          bestSession,
          bestAccount: { name: bestAccount[1].name, profit: bestAccount[1].profit },
          worstAccount: { name: worstAccount[1].name, profit: worstAccount[1].profit },
          averageRR,
          averageDuration,
          mostActiveDay,
          consistencyScore,
          equityCurve: { labels: sortedMonths, data: equityData },
        });
        setLoading(false);
        // Set default year to the most recent
        const years = [...new Set(sortedMonths.map(month => month.split('-')[0]))];
        setSelectedYear(years[years.length - 1]);
      } catch (error) {
        console.error('Error fetching aggregate data:', error);
        setLoading(false);
      }
    };
    fetchAggregateData();
  }, [userID, tradeAccounts]);

  if (loading) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}><MDTypography>Loading...</MDTypography></MDBox>
      <Footer />
    </DashboardLayout>
  );
  // Get unique years from equityCurve labels
  const availableYears = [...new Set(metrics.equityCurve.labels.map(label => label.split('-')[0]))].sort();

  // Filter equity curve data by selected year
  const filteredLabels = metrics.equityCurve.labels.filter(label => label.startsWith(selectedYear));
  const filteredData = metrics.equityCurve.data.slice(
    metrics.equityCurve.labels.indexOf(filteredLabels[0]),
    metrics.equityCurve.labels.indexOf(filteredLabels[filteredLabels.length - 1]) + 1
  );
  const equityChartData = {
    labels: metrics.equityCurve.labels,
    datasets: [{
      label: 'Equity Growth',
      data: metrics.equityCurve.data,
      fill: false,
      borderColor: theme.palette.success.main,
      tension: 0.3,
      pointRadius: 3,
      pointBackgroundColor: theme.palette.success.main,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { ticks: { maxRotation: 45, minRotation: 45 } },
      y: { beginAtZero: false, title: { display: true, text: 'Equity ($)' } },
    },
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: `Equity Growth Across All Accounts (${selectedYear})` },
    },
  };

  const MetricCard = ({ title, value, icon }) => (
    <Card style={{ minHeight: '100px' }}>
      <MDBox
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        pt={2}
        px={2}
        height="100%"
      >
        <MDBox display="flex" alignItems="center" mb={1}>
          {icon}
          <MDTypography variant="h6" fontWeight="medium" ml={1}>
            {title}
          </MDTypography>
        </MDBox>
        <MDTypography variant="h4" fontWeight="bold">
          {value}
        </MDTypography>
      </MDBox>
    </Card>
  );
  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDTypography variant="h4" mb={3} textAlign="center">
          Trade Accounts Overview (Comparing All Accounts)
        </MDTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Total Capital"
              value={`$${metrics.totalCapital.toFixed(2)}`}
              icon={<AccountBalanceIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Total Win Rate"
              value={`${metrics.totalWinRate.toFixed(2)}%`}
              icon={<TrendingUpIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Total Profit Factor"
              value={metrics.totalProfitFactor.toFixed(2)}
              icon={<ShowChartIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Total Trades"
              value={`${metrics.totalTrades.total} (W: ${metrics.totalTrades.wins}, L: ${metrics.totalTrades.losses}, B: ${metrics.totalTrades.breakevens})`}
              icon={<AssessmentIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Best Strategy"
              value={metrics.bestStrategy}
              icon={<StarIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Best Trading Session"
              value={metrics.bestSession}
              icon={<ScheduleIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Best Performing Account"
              value={`${metrics.bestAccount.name} ($${metrics.bestAccount.profit.toFixed(2)})`}
              icon={<StarIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Worst Performing Account"
              value={`${metrics.worstAccount.name} ($${metrics.worstAccount.profit.toFixed(2)})`}
              icon={<TrendingDownIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Average R:R"
              value={metrics.averageRR.toFixed(2)}
              icon={<ShowChartIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Avg Trade Duration"
              value={`${metrics.averageDuration.toFixed(2)} mins`}
              icon={<ScheduleIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Most Active Day"
              value={metrics.mostActiveDay}
              icon={<CalendarTodayIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MetricCard
              title="Consistency Score"
              value={`${metrics.consistencyScore} green months`}
              icon={<TrendingUpIcon color="inherit" />}
            />
          </Grid>
          <Grid item xs={12}>
            <Card>
              <MDBox p={2}>
                <MDBox display="flex" justifyContent="flex-end" mb={2}>
                  <Select
                    value={selectedYear || ''}
                    onChange={handleYearChange}
                    displayEmpty
                    sx={{ minWidth: 120 }}
                  >
                    {availableYears.map(year => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </MDBox>
                <MDBox height="400px">
                  <Line data={equityChartData} options={chartOptions} />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

TradeAccountsOverview.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccounts: PropTypes.array.isRequired,
};

export default TradeAccountsOverview;